import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

/**
 * The Dashboard's per-person preferences (Jason's review of 16 Oct): which
 * cards are collapsed or hidden, and which Gone quiet tickets are snoozed
 * until when. Kept in this browser, under the person's id, so two people on
 * one computer keep their own; a private window or cleared storage starts
 * afresh, which costs nothing but the arrangement.
 */
export type DashboardPrefs = {
  collapsed: ReadonlySet<string>;
  hidden: ReadonlySet<string>;
  toggleCollapsed: (card: string) => void;
  hide: (card: string) => void;
  showAll: () => void;
  /** A snoozed ticket's id is left out of Gone quiet until its time passes. */
  snoozedUntil: (ticketId: string) => number | null;
  snooze: (ticketId: string, days: number) => void;
};

type Stored = { collapsed: string[]; hidden: string[]; snoozed: Record<string, number> };
const EMPTY: Stored = { collapsed: [], hidden: [], snoozed: {} };

const keyFor = (userId: string) => `toado.dashboard.${userId}`;

function load(userId: string): Stored {
  try {
    const raw = window.localStorage.getItem(keyFor(userId));
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw) as Partial<Stored>;
    const now = Date.now();
    return {
      collapsed: Array.isArray(parsed.collapsed) ? parsed.collapsed.filter((id): id is string => typeof id === 'string') : [],
      hidden: Array.isArray(parsed.hidden) ? parsed.hidden.filter((id): id is string => typeof id === 'string') : [],
      // Snoozes that have run out are dropped on read, so the list never grows.
      snoozed: Object.fromEntries(Object.entries(parsed.snoozed ?? {}).filter(([, until]) => typeof until === 'number' && until > now)),
    };
  } catch {
    return EMPTY;
  }
}

function save(userId: string, stored: Stored) {
  try {
    window.localStorage.setItem(keyFor(userId), JSON.stringify(stored));
  } catch { /* Storage refused: the arrangement lasts this visit. */ }
}

const PrefsContext = createContext<DashboardPrefs | null>(null);

export function DashboardPrefsProvider({ userId, children }: { userId: string; children: ReactNode }) {
  const [stored, setStored] = useState<Stored>(() => load(userId));
  const update = useCallback((change: (current: Stored) => Stored) => {
    setStored((current) => {
      const next = change(current);
      save(userId, next);
      return next;
    });
  }, [userId]);
  const prefs = useMemo<DashboardPrefs>(() => ({
    collapsed: new Set(stored.collapsed),
    hidden: new Set(stored.hidden),
    toggleCollapsed: (card) => update((current) => ({
      ...current,
      collapsed: current.collapsed.includes(card) ? current.collapsed.filter((id) => id !== card) : [...current.collapsed, card],
    })),
    hide: (card) => update((current) => ({ ...current, hidden: [...new Set([...current.hidden, card])] })),
    showAll: () => update((current) => ({ ...current, hidden: [] })),
    snoozedUntil: (ticketId) => {
      const until = stored.snoozed[ticketId];
      return until && until > Date.now() ? until : null;
    },
    snooze: (ticketId, days) => update((current) => ({ ...current, snoozed: { ...current.snoozed, [ticketId]: Date.now() + days * 864e5 } })),
  }), [stored, update]);
  return <PrefsContext.Provider value={prefs}>{children}</PrefsContext.Provider>;
}

/** The Dashboard's preferences, or null outside the Dashboard (a card on Reports has none). */
export function useDashboardPrefs(): DashboardPrefs | null {
  return useContext(PrefsContext);
}
